import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';

import { MatCardModule } from '@angular/material/card';
import { Router } from '@angular/router';
import { DynamicTableComponent } from '../../dynamic-table/dynamic-table.component';
import { environment } from '../../../environment/environment';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

@Component({
  selector: 'app-duna-cleaning-unique-events',
  standalone: true,
  imports: [MatCardModule, DynamicTableComponent],
  templateUrl: './duna-cleaning-unique-events.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-cleaning-unique-events.component.scss',
})
export class DunaCleaningUniqueEventsComponent implements OnInit, OnDestroy {
  linkUuid: string | null = null;
  linkErrorMessage: string | null = null;
  linkValidationPending = false;

  private destroyed = false;
  private validationAbort: AbortController | null = null;

  constructor(private router: Router, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    const urlWithoutQuery = (this.router.url || '').split('?')[0];
    this.linkUuid =
      urlWithoutQuery.split('/').find((segment) => UUID_REGEX.test(segment)) ||
      null;

    if (!this.linkUuid) {
      this.linkErrorMessage =
        'Der blev ikke fundet et gyldigt rengørings-link i adressen. Et korrekt link ser ud som /cleaning/efterfulgt af et UUID.';
      this.cdr.detectChanges();
      return;
    }

    this.validateLinkExists(this.linkUuid);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.validationAbort?.abort();
  }

  get showEventTables(): boolean {
    return (
      !!this.linkUuid && !this.linkErrorMessage && !this.linkValidationPending
    );
  }

  private validateLinkExists(uuid: string): void {
    this.validationAbort?.abort();
    this.validationAbort = new AbortController();
    const signal = this.validationAbort.signal;

    let timedOut = false;
    const timeoutMs = 20000;
    const timeoutId = window.setTimeout(() => {
      timedOut = true;
      this.validationAbort?.abort();
    }, timeoutMs);

    this.linkValidationPending = true;
    this.linkErrorMessage = null;
    this.cdr.detectChanges();

    const qs = new URLSearchParams({
      page: '1',
      pageSize: '1',
      filter_period: 'future',
    });
    const url = `${
      environment.apiUrl
    }/events/cleaning/unique-links/${encodeURIComponent(
      uuid
    )}/events?${qs.toString()}`;

    fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'omit',
      signal,
    })
      .then(async (res) => {
        window.clearTimeout(timeoutId);
        if (this.destroyed) return;

        let bodyText = '';
        try {
          bodyText = await res.text();
        } catch {
          bodyText = '';
        }

        const parsed = this.tryParseRecordFromBody(bodyText);

        const rawMsg = parsed?.['message'];
        const rawDetail = parsed?.['detail'];
        const apiMessage = typeof rawMsg === 'string' ? rawMsg.trim() : '';
        const apiDetail = typeof rawDetail === 'string' ? rawDetail.trim() : '';
        const combinedApiText = `${apiMessage} ${apiDetail}`.toLowerCase();

        const looksLikeLinkMissing =
          res.status === 404 ||
          /\blink\s+not\s+found\b/i.test(apiMessage) ||
          /\blink\s+not\s+found\b/i.test(combinedApiText);

        if (looksLikeLinkMissing) {
          this.linkErrorMessage =
            'Dette rengørings-link findes ikke eller er ikke længere gyldigt. Tjek at du har kopieret hele linket, eller kontakt din administrator.';
          return;
        }

        if (res.status === 400) {
          this.linkErrorMessage =
            apiMessage ||
            'Linket i adressen er ikke et gyldigt UUID. Et korrekt link ser ud som /cleaning/efterfulgt af et UUID.';
          return;
        }

        if (!res.ok) {
          this.linkErrorMessage =
            apiMessage ||
            apiDetail ||
            'Linket kunne ikke bekræfte mod serveren. Prøv igen senere, eller kontakt din administrator hvis problemet fortsætter.';
          return;
        }

        const meta = parsed?.['meta'];
        if (parsed !== null && typeof meta === 'object' && meta !== null) {
          return;
        }

        this.linkErrorMessage =
          parsed === null
            ? 'Der kom et uventet svar fra serveren (kunne ikke læse JSON — evt. HTML eller PHP-advarsler foran svaret). Linket kan stadig være gyldigt; prøv at genindlæse eller kontakt administrator.'
            : 'Rengørings-linket kunne ikke bekræftes korrekt mod serveren (svar manglede forventet liste-format). Prøv igen senere eller kontakt din administrator.';
      })
      .catch((err: unknown) => {
        window.clearTimeout(timeoutId);
        if (this.destroyed) return;

        const name =
          err && typeof err === 'object' && 'name' in err
            ? String((err as { name: string }).name)
            : '';
        if (name === 'AbortError') {
          if (timedOut) {
            this.linkErrorMessage = `Serveren svarede ikke inden for ${
              timeoutMs / 1000
            } sekunder. Tjek din forbindelse og prøv igen.`;
          } else {
            this.linkErrorMessage =
              'Forbindelsen blev afbrudt før serveren svarede. Genindlæs siden — linket kan stadig være ugyldigt eller tilbagekaldt.';
          }
          return;
        }
        this.linkErrorMessage =
          'Linket kunne ikke bekræfte mod serveren (nettet eller CORS blokerede måske kaldet). Prøv igen senere, eller kontakt din administrator.';
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
        if (!this.destroyed) {
          this.linkValidationPending = false;
          this.cdr.detectChanges();
        }
      });
  }

  private tryParseRecordFromBody(
    rawBody: string
  ): Record<string, unknown> | null {
    const trimmed = rawBody.trim();
    if (!trimmed) return null;

    const candidates: string[] = [trimmed];
    const firstObjectStart = trimmed.indexOf('{');
    const lastObjectEnd = trimmed.lastIndexOf('}');
    if (firstObjectStart !== -1 && lastObjectEnd > firstObjectStart) {
      candidates.push(trimmed.slice(firstObjectStart, lastObjectEnd + 1));
    }

    const trailingJsonMatch = trimmed.match(/\{[\s\S]*\}\s*$/);
    if (trailingJsonMatch?.[0]) {
      candidates.push(trailingJsonMatch[0].trim());
    }

    for (const candidate of Array.from(new Set(candidates))) {
      try {
        const value = JSON.parse(candidate) as unknown;
        if (
          value !== null &&
          typeof value === 'object' &&
          !Array.isArray(value)
        ) {
          return value as Record<string, unknown>;
        }
      } catch {
        /* prøv næste kandidat */
      }
    }

    return null;
  }
}
